import { useEffect } from 'react';

import { useRouter } from '@boss/hooks';

import { isB2b } from '../../utils';
import { RBAC_PERMISSION_COOKIE, serializePermissionCookie } from '../../utils/rbac/permission-cookie';
import { useContactPermissions } from '../use-contact-permissions/use-contact-permissions';
import { useProfile } from '../use-profile/use-profile';

// max-age is 24h by design: when the cookie expires the server-side guard silently
// degrades to the client-side guard backstop — do not "fix" this by making it permanent.
const setPermissionCookie = (value: string) => {
  document.cookie = `${RBAC_PERMISSION_COOKIE}=${value}; path=/; max-age=86400; samesite=lax`;
};

const clearPermissionCookie = () => {
  document.cookie = `${RBAC_PERMISSION_COOKIE}=; path=/; max-age=0`;
};

// [44843] Persist resolved RBAC permissions so the middleware can serve the silent
// server-side redirect on order-guarded routes (winkelmandje/checkout) instead of
// the slow client-side bounce. Only writes once the permissions are definitively
// resolved (never the pre-resolution pin); clears on logged-out / B2C.
export const usePermissionCookie = (): void => {
  const { locale } = useRouter();
  const { isLoggedIn } = useProfile();
  const { showprice = true, canorder = true, loading = true } = useContactPermissions(locale) ?? {};

  useEffect(() => {
    if (!isB2b || !isLoggedIn) {
      clearPermissionCookie();
      return;
    }

    if (loading) {
      // Pre-resolution pin — no definitive answer yet, keep the current cookie.
      return;
    }

    setPermissionCookie(serializePermissionCookie({ showprice, canorder }));
  }, [isLoggedIn, loading, showprice, canorder]);
};

export const PermissionCookieWriter = () => {
  usePermissionCookie();
  return null;
};
